import React, { useEffect, useRef } from 'react'
import { Modal, Button, Alert } from 'react-bootstrap'
import { AlertTriangle, Info, CheckCircle2, XCircle, Eye } from 'lucide-react'

interface SubmitPredictionsModalProps {
  show: boolean
  onHide: () => void
  onConfirm: () => void
  inspectCount: number
  acceptedCount: number
  rejectedCount: number
}

const SubmitPredictionsModal: React.FC<SubmitPredictionsModalProps> = ({
  show,
  onHide,
  onConfirm,
  inspectCount,
  acceptedCount,
  rejectedCount,
}) => {
  const totalAccepted = inspectCount + acceptedCount
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!show || !modalRef.current) return
    const node = modalRef.current
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key
      if (key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onHide()
        return
      }
      if (key === 'Enter') {
        // If a focusable control inside the modal is active, let the default click happen
        const active = document.activeElement as HTMLElement | null
        const isFocusableTag = (el: HTMLElement | null) => {
          if (!el) return false
          const tag = el.tagName
          return (
            tag === 'BUTTON' ||
            tag === 'A' ||
            tag === 'INPUT' ||
            tag === 'TEXTAREA' ||
            tag === 'SELECT'
          )
        }
        const activeInsideModal = !!active && node.contains(active)
        if (activeInsideModal && isFocusableTag(active)) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        onConfirm()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [show, onHide, onConfirm])

  const hasUntouchedItems = inspectCount > 0

  return (
    <Modal show={show} onHide={onHide} centered keyboard={false} backdrop="static" size="lg">
      <div
        ref={modalRef}
        onKeyDownCapture={(e) => {
          if (e.key === 'Tab') {
            e.stopPropagation()
          }
        }}
      >
        <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-0">
          <Modal.Title className="d-flex align-items-center gap-2">
            <CheckCircle2 size={24} />
            Submit Review
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Summary Section */}
          <div className="mb-4">
            <h6 className="text-light mb-3 fw-bold">Review Summary</h6>
            <div className="d-flex flex-column gap-2">
              <div
                className="d-flex align-items-center justify-content-between p-3 rounded"
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <CheckCircle2 size={18} style={{ color: '#10b981' }} />
                  <span className="fw-semibold">Accepted</span>
                </div>
                <span className="badge bg-success">{acceptedCount}</span>
              </div>
              <div
                className="d-flex align-items-center justify-content-between p-3 rounded"
                style={{
                  backgroundColor: 'rgba(245, 158, 11, 0.15)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <Eye size={18} style={{ color: '#f59e0b' }} />
                  <span className="fw-semibold">Review (Auto-Accept)</span>
                </div>
                <span className="badge bg-warning text-dark">{inspectCount}</span>
              </div>
              <div
                className="d-flex align-items-center justify-content-between p-3 rounded"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <XCircle size={18} style={{ color: '#ef4444' }} />
                  <span className="fw-semibold">Rejected</span>
                </div>
                <span className="badge bg-danger">{rejectedCount}</span>
              </div>
              <div
                className="d-flex align-items-center justify-content-between p-3 rounded mt-2"
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.15)',
                  border: '2px solid rgba(59, 130, 246, 0.4)',
                }}
              >
                <span className="fw-bold text-light">Total Annotations to Submit</span>
                <span className="badge bg-primary fs-6">{totalAccepted}</span>
              </div>
            </div>
          </div>

          {/* Warning if there are untouched items */}
          {hasUntouchedItems && (
            <Alert variant="warning" className="mb-3 d-flex align-items-start gap-2">
              <AlertTriangle size={20} className="flex-shrink-0 mt-1" />
              <div>
                <strong className="d-block mb-1">Unreviewed Items Will Be Auto-Accepted</strong>
                <p className="mb-0 small">
                  You have <strong>{inspectCount}</strong> item{inspectCount !== 1 ? 's' : ''} still
                  in the <strong>Review</strong> tab. These predictions will be automatically
                  accepted and may contain errors. We recommend reviewing all items before
                  submitting to ensure accuracy.
                </p>
              </div>
            </Alert>
          )}

          {/* Important Information */}
          <Alert variant="info" className="mb-0 d-flex align-items-start gap-2">
            <Info size={20} className="flex-shrink-0 mt-1" />
            <div>
              <strong className="d-block mb-1">AI May Miss Some Objects</strong>
              <p className="mb-2 small">
                The AI model may not detect all objects in the image. Before submitting, please:
              </p>
              <ul className="mb-0 small ps-3">
                <li>Review the entire image carefully</li>
                <li>Add any missing annotations that the AI didn't detect</li>
                <li>Check for objects in shadows, edges, or unusual angles</li>
                <li>Verify that all relevant objects have been annotated</li>
              </ul>
            </div>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel (Esc)
          </Button>
          <Button variant="primary" onClick={onConfirm} autoFocus>
            Submit Review (Enter)
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  )
}

export default SubmitPredictionsModal
